import { Box, type BoxProps } from "@mui/material";

import type { ReactNode } from "react";

import { EditOutlinedIcon, type AppIconComponent } from "@/icons";

export interface MergeTagChipProps extends Omit<BoxProps, "component" | "sx"> {
  /** Content shown inside the chip (usually a formatted merge-tag label). */
  label: ReactNode;
  /**
   * Editor variant: pointer cursor, hover/focus states and a leading edit
   * icon. Interaction wiring (onClick, onKeyDown, role, aria-*) is passed
   * through via the spread `BoxProps`.
   */
  interactive?: boolean;
  /**
   * Leading icon that marks where the tag's value comes from (e.g. the
   * blueprint glyph for generated content). Takes the place of the
   * interactive edit icon when both apply; sized and spaced by the chip.
   */
  icon?: AppIconComponent;
}

/**
 * The merge-tag chip's shared look: an inline `info`-colored pill that flows
 * with surrounding text. Presentational only — no editor/Lexical coupling — so
 * it works anywhere (RTE, subject/preheader, broadcast reports). Pass
 * `interactive` plus the relevant handlers for the editable editor variant.
 */
export function MergeTagChip({
  label,
  interactive = false,
  icon,
  ...boxProps
}: MergeTagChipProps) {
  const LeadingIcon = icon ?? (interactive ? EditOutlinedIcon : undefined);
  return (
    <Box
      component="span"
      {...boxProps}
      sx={[
        (theme) => ({
          display: "inline",
          verticalAlign: "baseline",
          backgroundColor: theme.palette.info.bgContainer,
          color: theme.palette.info.main,
          borderRadius: `${theme.shape.borderRadiusSm}px`,
          px: "6px",
          boxDecorationBreak: "clone",
          fontSize: "inherit",
          lineHeight: "inherit",
          fontWeight: 500,
          whiteSpace: "nowrap",
        }),
        interactive &&
          ((theme) => ({
            userSelect: "none",
            cursor: "pointer",
            "&:hover": { backgroundColor: theme.palette.info.light },
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.info.main}`,
              outlineOffset: "1px",
            },
          })),
      ]}
    >
      {LeadingIcon ? (
        <LeadingIcon
          sx={{
            fontSize: "14px",
            verticalAlign: "middle",
            mr: "4px",
            position: "relative",
            top: "-1px",
          }}
        />
      ) : null}
      {label}
    </Box>
  );
}
